import { CheckCircle, Import, ReceiptText, Record, Search, X } from 'reicon-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Skeleton } from 'boneyard-js/react';
import { Badge, type TomBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CabecalhoOrdenavel,
  useOrdenacaoDeTabela,
  type OrdenacaoAtiva,
  type ValoresDeColuna,
} from '@/components/ui/cabecalho-ordenavel';
import { ControlePaginacao } from '@/components/ui/controle-paginacao';
import { FiltroDeData } from '@/components/ui/filtro-de-data';
import {
  MOTIVO_PERIODO_MAIOR_QUE_UM_ANO,
  periodoPadrao,
  umAnoAntes,
  umAnoDepois,
} from '@/lib/periodoDeBusca';
import { cn } from '@/lib/utils';
import { useFocoDeModal } from '@/lib/useFocoDeModal';
import { DURACAO_SAIDA_MODAL_MS, usePresenca } from '@/lib/usePresenca';
import {
  classificarDocumentoOrigem,
  formatarNumeroDocumentoOrigem,
  type TipoDocumentoOrigem,
} from '../../domain/dav/documentoOrigem';
import { formatarCentavos } from '../../domain/precificacao/dinheiro';
import { useListaDavs, type DavListado } from '../../services/dav/davQueries';
import type { ImportacaoVendaDeps } from '../../services/importacao/importarVendaExistente';
import { ITENS_POR_PAGINA } from '../../services/paginacao';
import { useImportacaoDav } from './useImportacaoDav';

/**
 * Janela de importação de DAV (T008/T018, `DAV-01`/`DAV-02`) — réplica do frame
 * "PDV Online Web - Modal DAV" do Pencil (`design/CentriumCheckout.pen`, nó
 * `jfZtk`/`Hao17`): 1120×720, raio 24, cabeçalho de 78px com ícone
 * `receipt-text`, faixa de filtros de 14/24 de folga, tabela com cabeçalho de
 * 38px e linhas de 52px, rodapé de 60px com paginação à esquerda e ações à
 * direita.
 *
 * Desktop-only (AD-046): não há equivalente no wizard mobile, mesma decisão já
 * tomada para o modal de recuperação de NFCe (011).
 *
 * **Quatro ausências deliberadas em relação ao mockup**, todas por falta de
 * dado real no contrato do ERP (AD-024/AD-095), mesmo critério já aplicado ao
 * modal de cliente (AD-093):
 *
 * - Filtros "Status", "Vendedor", "Tipo" e "Origem" — `ListaDAVs` não tem
 *   nenhum parâmetro correspondente; só `Txtbusca` e o período de emissão
 *   existem. Desenhá-los produziria controles que não filtram nada.
 * - Coluna "Status" — `CheckoutListaDAVs.DAV_DAV` não tem o campo. Exibi-la
 *   exigiria inventar o estado do documento. A "Origem" existe desde 2026-10-01
 *   (AD-258): é a coluna "Documento de Origem", com número/série e o tipo do
 *   documento em badge, e a "Senha" virou coluna no mesmo pedido.
 * - Ação de reimpressão por linha — proibida por `FR-009`/AD-035, removida
 *   ainda na fase de plano.
 * - O nome do vendedor na coluna "Cliente", **quando o ERP não o manda**: o
 *   SDT tem `VendedorNome` (AD-172), mas o ERP de 2026-09-14 ainda o devolve
 *   vazio (pendência 57, AD-237). A linha usa o nome assim que ele vier, e até
 *   lá exibe "Vendedor #&lt;código&gt;".
 *
 * **Um desvio de rótulo**: o botão do rodapé é "Importar DAV", não "Faturar
 * DAV" como no desenho. Confirmar aqui **não fatura** — popula o carrinho, e a
 * venda segue o fluxo normal de pagamento e finalização (`FR-008`). Rotular de
 * "Faturar" prometeria ao operador uma emissão que este clique não faz.
 */

export interface ModalImportacaoDavProps {
  readonly aberto: boolean;
  readonly onFechar: () => void;
  /** Portas injetáveis em teste (stubs das features 008/012, rede). */
  readonly deps?: Partial<ImportacaoVendaDeps>;
}

/** Mesmo debounce dos demais modais de busca desta base. */
const DEBOUNCE_BUSCA_MS = 300;

type ColunaDav = 'dav' | 'documento' | 'senha' | 'cliente' | 'emissao' | 'total';

/**
 * Cor da badge por tipo de documento de origem (pedido do usuário,
 * 2026-10-01 — "cores distintas", AD-258). As três famílias são as do Pencil
 * (`$info`, `$warning`, `$success`); um tipo desconhecido fica neutro.
 */
const TOM_DO_DOCUMENTO: Readonly<Record<TipoDocumentoOrigem, TomBadge>> = {
  PEDIDO: 'info',
  ORCAMENTO: 'aviso',
  ORDEM_SERVICO: 'sucesso',
  OUTRO: 'neutro',
};

/** Largura do número no texto de ordenação — agrupa por tipo e ordena o número como número. */
const DIGITOS_ORDENACAO_DOCUMENTO = 15;

/**
 * O valor que cada coluna compara ao ordenar a página (ver
 * `useOrdenacaoDeTabela`). Constante de módulo de propósito: o mapa entra nas
 * dependências do `useMemo` que ordena.
 *
 * `dataEmissao` é comparada crua (`YYYY-MM-DD`, como o ERP devolve) e não pelo
 * `DD/MM/AAAA` que a célula exibe — o formato brasileiro ordenaria por dia do
 * mês, misturando anos.
 */
const VALORES_DE_COLUNA_DAV: ValoresDeColuna<DavListado, ColunaDav> = {
  dav: (dav) => dav.numeroDav,
  // Tipo primeiro, número com zeros à esquerda depois: ordenar pela coluna
  // agrupa pedidos, orçamentos e O.S., e dentro de cada grupo `1287` vem depois
  // de `999` — a comparação crua de texto inverteria os dois.
  documento: (dav) =>
    `${classificarDocumentoOrigem(dav.titulo).rotulo} ${dav.documentoOrigemNumero.padStart(DIGITOS_ORDENACAO_DOCUMENTO, '0')}`,
  senha: (dav) => dav.senha,
  cliente: (dav) => dav.clienteNome,
  emissao: (dav) => dav.dataEmissao,
  total: (dav) => dav.valorTotal,
};

/** Identidade estável para a página ainda não carregada — sem ela o `useMemo` da ordenação reinicia a cada render. */
const SEM_DAVS: readonly DavListado[] = [];

/** `YYYY-MM-DD` (contrato) → `DD/MM/AAAA` (leitura do operador). */
function formatarDataEmissao(iso: string): string {
  const partes = iso.split('-');
  const [ano, mes, dia] = partes;
  if (partes.length !== 3 || ano === undefined || mes === undefined || dia === undefined) {
    // Formato inesperado: exibe cru em vez de esconder o dado. O Checkout não
    // reinterpreta o que o ERP mandou (Constitution III).
    return iso;
  }
  return `${dia}/${mes}/${ano}`;
}

export function ModalImportacaoDav({
  aberto,
  onFechar,
  deps = {},
}: ModalImportacaoDavProps): ReactElement | null {
  const [termo, setTermo] = useState('');
  const [termoDebounced, setTermoDebounced] = useState('');
  const [dataInicial, setDataInicial] = useState(() => periodoPadrao().inicial);
  const [dataFinal, setDataFinal] = useState(() => periodoPadrao().final);
  const [pagina, setPagina] = useState(1);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [importando, setImportando] = useState(false);

  const [abertoAnterior, setAbertoAnterior] = useState(aberto);
  if (aberto !== abertoAnterior) {
    setAbertoAnterior(aberto);
    if (aberto) {
      const periodo = periodoPadrao();
      setTermo('');
      setTermoDebounced('');
      // Recalculado a cada abertura, não só na montagem: o Checkout fica aberto
      // o turno inteiro, e uma janela montada ontem ofereceria o período de
      // ontem ao operador que a reabre hoje.
      setDataInicial(periodo.inicial);
      setDataFinal(periodo.final);
      setPagina(1);
      setSelecionado(null);
    }
  }

  useEffect(() => {
    const temporizador = setTimeout(() => {
      setTermoDebounced(termo);
    }, DEBOUNCE_BUSCA_MS);
    return () => {
      clearTimeout(temporizador);
    };
  }, [termo]);

  // ESC fecha a janela (pedido do usuário, 2026-09-03). O ouvinte é de
  // `window`, e não um `onKeyDown` no backdrop como antes: aquele só disparava
  // com o foco dentro do modal, e bastava um clique no fundo — ou o calendário
  // de um filtro devolvendo o foco ao `body` — para a tecla não fazer nada.
  // Nada foi importado neste ponto, então sair é sempre seguro.
  useEffect(() => {
    if (!aberto) {
      return;
    }
    const aoTeclar = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') {
        onFechar();
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => {
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, onFechar]);

  // Ao contrário da busca de cliente/produto, não há piso de caracteres: termo
  // vazio é uma consulta legítima — "todos os DAVs prontos para faturamento" é
  // exatamente o que o operador vê ao abrir a janela (`FR-001`).
  const lista = useListaDavs({ txtBusca: termoDebounced, dataInicial, dataFinal, pagina }, aberto);

  const { importar } = useImportacaoDav(deps);
  const { montado, saindo } = usePresenca(aberto, DURACAO_SAIDA_MODAL_MS);
  // Foco inicial declarado, e não `autoFocus` (feature 016): ver
  // `OpcoesFocoDeModal.focoInicial`.
  const campoBusca = useRef<HTMLInputElement>(null);
  const janelaRef = useFocoDeModal<HTMLDivElement>(aberto, { focoInicial: campoBusca });

  // A ordenação mora aqui, e não dentro de `TabelaDeDavs`: durante o
  // `isFetching` da página seguinte a tabela dá lugar ao skeleton e desmonta —
  // um estado local nela perderia a coluna escolhida justo na troca de página,
  // que é onde o operador espera que ela continue valendo.
  const {
    linhas: davs,
    ordenacao,
    alternar: alternarOrdenacao,
  } = useOrdenacaoDeTabela(lista.data?.davs ?? SEM_DAVS, VALORES_DE_COLUNA_DAV);

  if (!montado) {
    return null;
  }

  const davSelecionado = davs.find((dav) => dav.numeroDav === selecionado) ?? null;
  const semResultado = lista.data !== undefined && davs.length === 0;

  /**
   * Trocar qualquer uma das datas reinicia a paginação e solta a seleção: a
   * linha escolhida pode não existir no novo período, e importar o índice
   * antigo seria o documento errado.
   */
  function aoTrocarData(definir: (iso: string) => void): (iso: string) => void {
    return (iso) => {
      definir(iso);
      setPagina(1);
      setSelecionado(null);
    };
  }

  async function confirmarImportacao(): Promise<void> {
    if (davSelecionado === null || importando) {
      return;
    }
    setImportando(true);
    try {
      // Sucesso fecha a janela; erro a mantém aberta, com o toast já exibido
      // pelo hook e o carrinho intacto (D7/AD-052).
      if (await importar(davSelecionado)) {
        onFechar();
      }
    } finally {
      setImportando(false);
    }
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center bg-[color-mix(in_srgb,var(--cc-color-ink)_40%,transparent)] p-lg',
        saindo ? 'cc-backdrop-sai' : 'cc-backdrop-entra',
      )}
      data-testid="modal-importacao-dav"
      onKeyDown={(evento) => {
        // Enter importa o documento já selecionado (pedido do usuário,
        // 2026-09-03) — o mesmo que clicar em "Importar DAV", de qualquer
        // ponto da janela. A linha da tabela trata a tecla por conta própria e
        // interrompe a propagação: lá o Enter ainda pode significar "selecionar
        // esta linha", e importar a linha **anterior** seria o documento errado.
        if (evento.key !== 'Enter') {
          return;
        }
        // Enter **em cima de um botão** é o clique daquele botão, e nada mais:
        // sem esta guarda o evento borbulhava até aqui e a janela importava o
        // documento selecionado antes de executar a ação escolhida — teclar
        // Enter em "Cancelar" importava e só então fechava, que é o oposto do
        // gesto do operador. As linhas da tabela já se defendiam com
        // `stopPropagation`; o rodapé e o "X" não.
        if (evento.target instanceof HTMLElement && evento.target.closest('button') !== null) {
          return;
        }
        // Fora dos botões, Enter importa o documento já selecionado de
        // qualquer ponto da janela (pedido do usuário, 2026-09-03) — o mesmo
        // que clicar em "Importar DAV".
        void confirmarImportacao();
      }}
    >
      <div
        ref={janelaRef}
        role="dialog"
        aria-modal="true"
        aria-label="Importação de DAV"
        className={cn(
          'flex max-h-full w-full max-w-[1120px] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-lg',
          saindo ? 'cc-modal-sai' : 'cc-modal-entra',
        )}
      >
        <header className="flex h-[78px] shrink-0 items-center justify-between gap-sm border-b border-border px-lg">
          <div className="flex items-center gap-sm">
            <span className="flex size-[42px] shrink-0 items-center justify-center rounded-full bg-secondary">
              <ReceiptText className="size-5 text-primary" aria-hidden="true" />
            </span>
            <div className="flex flex-col gap-[2px]">
              <h2 className="text-xl font-semibold text-foreground">Importação de DAV</h2>
              <p className="text-sm font-medium text-muted-foreground">
                Selecione um documento para importar para a venda
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="icon-lg"
            className="shrink-0 rounded-full"
            aria-label="Fechar"
            onClick={onFechar}
          >
            <X className="size-4.5" aria-hidden="true" />
          </Button>
        </header>

        <div className="flex shrink-0 flex-col gap-[10px] border-b border-border px-lg py-[14px]">
          <div className="flex items-center gap-[10px]">
            <label className="flex h-11 flex-1 items-center gap-xs rounded-full bg-secondary px-base text-md font-medium text-foreground">
              <Search className="size-4.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Termo de busca</span>
              <input
                className="h-full w-full bg-transparent outline-none placeholder:text-muted-foreground"
                data-testid="campo-busca-dav"
                ref={campoBusca}
                autoComplete="off"
                placeholder="Busque por número, título, cliente ou senha"
                value={termo}
                onChange={(evento) => {
                  setTermo(evento.target.value);
                  setPagina(1);
                  setSelecionado(null);
                }}
              />
            </label>

            {/* O Pencil desenha o período como **uma** pílula estática
                ("Emissão: 01/06 - 11/06"). Aqui ela vira duas pílulas
                independentes — início e fim, que é o que o contrato permite
                filtrar (`Datainicial`/`Datafinal`) —, cada uma com o seu
                calendário, que abre a qualquer clique no campo. A separação é
                pedido do usuário (2026-09-03): dentro de um invólucro só, as
                duas datas liam como um campo único e nada dizia qual metade
                estava sendo editada. Cada pílula conserva a forma do desenho
                (altura 36, raio total, superfície secundária, ícone
                `calendar-days`).

                As etiquetas dizem **"Data inicial"/"Data final"**, e não mais
                "Emissão de"/"até" (pedido do usuário, 2026-09-11): "até"
                sozinho só faz sentido lido em sequência com a pílula anterior,
                que é justamente a leitura de campo único que a separação veio
                desfazer. Cada pílula agora se explica isolada, e o vão entre
                elas (12) é maior que o que as separa da busca (10). */}
            <div className="flex shrink-0 items-center gap-sm">
              <FiltroDeData
                etiqueta="Data inicial"
                rotulo="Data inicial de emissão"
                testId="dav-data-inicial"
                valor={dataInicial}
                onChange={aoTrocarData(setDataInicial)}
                // No máximo um ano de período (pedido do usuário, 2026-09-24):
                // cada data limita a outra (`lib/periodoDeBusca.ts`).
                minimo={umAnoAntes(dataFinal)}
                motivoForaDoLimite={MOTIVO_PERIODO_MAIOR_QUE_UM_ANO}
              />
              <FiltroDeData
                etiqueta="Data final"
                rotulo="Data final de emissão"
                testId="dav-data-final"
                valor={dataFinal}
                onChange={aoTrocarData(setDataFinal)}
                maximo={umAnoDepois(dataInicial)}
                motivoForaDoLimite={MOTIVO_PERIODO_MAIOR_QUE_UM_ANO}
              />
            </div>
          </div>

          {lista.data === undefined ? null : (
            <p className="text-base font-semibold text-foreground" data-testid="contagem-dav">
              {lista.data.totalRegistros} DAV(s) encontrada(s)
            </p>
          )}
        </div>

        <div className="min-h-40 flex-1 overflow-y-auto" aria-live="polite">
          {lista.isPending || lista.isFetching ? (
            <Skeleton
              name="lista-davs"
              loading
              fixture={<EstruturaResultados />}
              fallback={<EstruturaResultados aria-hidden />}
            >
              <EstruturaResultados />
            </Skeleton>
          ) : lista.isError ? (
            <p className="p-base text-md text-destructive">
              Não foi possível carregar os documentos. Tente novamente.
            </p>
          ) : semResultado ? (
            <p className="p-base text-md text-muted-foreground" data-testid="dav-sem-resultados">
              Nenhum documento encontrado para os filtros informados.
            </p>
          ) : (
            <TabelaDeDavs
              davs={davs}
              ordenacao={ordenacao}
              selecionado={selecionado}
              onAlternarOrdenacao={alternarOrdenacao}
              onSelecionar={setSelecionado}
              onConfirmar={() => {
                void confirmarImportacao();
              }}
            />
          )}
        </div>

        <footer className="flex h-[60px] shrink-0 items-center justify-between gap-sm border-t border-border px-lg">
          <ControlePaginacao
            pagina={pagina}
            totalPaginas={lista.data?.totalPaginas}
            testIdPrefixo="dav"
            onTrocarPagina={(proxima) => {
              setPagina(proxima);
              setSelecionado(null);
            }}
          />

          <div className="flex items-center gap-[10px]">
            <Button
              type="button"
              className="h-11 w-[156px] gap-xs rounded-full text-md font-bold"
              data-testid="confirmar-importacao-dav"
              disabled={davSelecionado === null || importando}
              onClick={() => {
                void confirmarImportacao();
              }}
            >
              <Import className="size-4.5" aria-hidden="true" />
              Importar DAV
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

interface TabelaDeDavsProps {
  readonly davs: readonly DavListado[];
  readonly ordenacao: OrdenacaoAtiva<ColunaDav> | null;
  readonly selecionado: string | null;
  readonly onAlternarOrdenacao: (chave: ColunaDav) => void;
  readonly onSelecionar: (numeroDav: string) => void;
  /** Enter sobre a linha já selecionada — importa sem passar pelo rodapé. */
  readonly onConfirmar: () => void;
}

const classeCelulaCabecalho =
  'flex h-full items-center px-[10px] text-xs font-bold text-muted-foreground';

function TabelaDeDavs({
  davs,
  ordenacao,
  selecionado,
  onAlternarOrdenacao,
  onSelecionar,
  onConfirmar,
}: TabelaDeDavsProps): ReactElement {
  return (
    <div data-testid="resultados-dav">
      {/* O bloco deixou de ser `aria-hidden` ao ganhar os botões de ordenação:
          esconder um controle operável da árvore de acessibilidade tiraria a
          ordenação de quem navega por teclado ou leitor de tela. Só a primeira
          coluna — a do marcador de seleção, sem rótulo — segue oculta. */}
      <div className="flex h-[38px] border-y border-border bg-muted">
        <span className={cn(classeCelulaCabecalho, 'w-[42px]')} aria-hidden="true" />
        <CabecalhoOrdenavel
          chaveDaColuna="dav"
          rotulo="DAV"
          ordenacao={ordenacao}
          className="w-[124px] shrink-0"
          onAlternar={onAlternarOrdenacao}
        />
        <CabecalhoOrdenavel
          chaveDaColuna="documento"
          rotulo="Documento de Origem"
          ordenacao={ordenacao}
          className="w-[200px] shrink-0"
          onAlternar={onAlternarOrdenacao}
        />
        <CabecalhoOrdenavel
          chaveDaColuna="senha"
          rotulo="Senha"
          ordenacao={ordenacao}
          className="w-[104px] shrink-0"
          onAlternar={onAlternarOrdenacao}
        />
        <CabecalhoOrdenavel
          chaveDaColuna="cliente"
          rotulo="Cliente"
          ordenacao={ordenacao}
          className="min-w-0 flex-1"
          onAlternar={onAlternarOrdenacao}
        />
        <CabecalhoOrdenavel
          chaveDaColuna="emissao"
          rotulo="Emissão"
          ordenacao={ordenacao}
          className="w-[108px] shrink-0"
          onAlternar={onAlternarOrdenacao}
        />
        <CabecalhoOrdenavel
          chaveDaColuna="total"
          rotulo="Total"
          ordenacao={ordenacao}
          className="w-[116px] shrink-0"
          onAlternar={onAlternarOrdenacao}
        />
      </div>
      <ul>
        {davs.map((dav) => {
          const ativo = dav.numeroDav === selecionado;
          return (
            <li key={dav.numeroDav} className="border-b border-border last:border-b-0">
              <button
                type="button"
                data-testid="linha-dav"
                data-numero-dav={dav.numeroDav}
                aria-pressed={ativo}
                className={cn(
                  'flex h-10 w-full items-center text-left hover:bg-accent',
                  ativo ? 'bg-secondary' : 'bg-card',
                )}
                onClick={() => {
                  onSelecionar(dav.numeroDav);
                }}
                onKeyDown={(evento) => {
                  if (evento.key !== 'Enter') {
                    return;
                  }
                  // A tecla é resolvida aqui, e não pelo ouvinte da janela: no
                  // teclado o Enter chega à linha que **tem o foco**, que pode
                  // não ser a selecionada. Primeiro Enter escolhe a linha,
                  // segundo importa — dois passos, nunca o documento errado.
                  evento.preventDefault();
                  evento.stopPropagation();
                  if (ativo) {
                    onConfirmar();
                    return;
                  }
                  onSelecionar(dav.numeroDav);
                }}
              >
                <span className="flex w-[42px] shrink-0 items-center justify-center">
                  {ativo ? (
                    <CheckCircle className="size-4 text-primary" aria-hidden="true" />
                  ) : (
                    <Record className="size-4 text-muted-foreground/60" aria-hidden="true" />
                  )}
                </span>
                {/* `truncate` não é enfeite: sem ele o número transborda a
                    largura fixa e encosta na coluna "Documento" — foi o que o
                    operador viu em produção. A largura já comporta 14 dígitos
                    mono, bem acima do que o ERP devolve, então o corte é rede
                    de segurança, não comportamento esperado. */}
                <span className="w-[124px] shrink-0 truncate px-[10px] font-mono text-xs font-bold tabular-nums">
                  {dav.numeroDav}
                </span>
                <CelulaDocumentoOrigem dav={dav} />
                <span
                  className="w-[104px] shrink-0 truncate px-[10px] font-mono text-xs font-semibold tabular-nums"
                  data-testid="senha-dav"
                >
                  {dav.senha.trim() === '' ? '—' : dav.senha}
                </span>
                <span className="flex min-w-0 flex-1 flex-col px-[10px]">
                  <span className="truncate text-sm font-bold">{dav.clienteNome}</span>
                  {/* O nome quando o ERP o devolve (AD-172), o código como
                      recuo. Não é enfeite: "Vendedor #12" não distingue dois
                      DAVs para quem opera o caixa, e era o que AD-095 obrigava
                      a exibir enquanto `ListaDAVs` não tinha o campo. O recuo
                      permanece porque o ERP de 2026-09-14 ainda devolve o nome
                      vazio (pendência 57) — e continua valendo para um DAV sem
                      vendedor cadastrado. */}
                  <span className="truncate text-xs font-medium text-muted-foreground">
                    {dav.vendedorNome === null
                      ? `Vendedor #${dav.vendedorCodigo}`
                      : `Vendedor ${dav.vendedorNome}`}
                  </span>
                </span>
                <span className="w-[108px] shrink-0 px-[10px] font-mono text-xs font-semibold tabular-nums">
                  {formatarDataEmissao(dav.dataEmissao)}
                </span>
                <span className="w-[116px] shrink-0 px-[10px] font-mono text-sm font-bold tabular-nums">
                  {formatarCentavos(dav.valorTotal)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Coluna "Documento de Origem" (AD-258, pedido do usuário em 2026-10-01):
 * `Numero/Serie` em mono, seguido do tipo do documento numa badge colorida.
 *
 * A badge vem depois do número, e não antes, como o pedido descreve
 * ("Numero/Serie . Badge"): o número é o que o operador procura com os olhos, e
 * alinhado à esquerda ele fica na mesma vertical em todas as linhas.
 */
function CelulaDocumentoOrigem({ dav }: { readonly dav: DavListado }): ReactElement {
  const { tipo, rotulo } = classificarDocumentoOrigem(dav.titulo);
  const numero = formatarNumeroDocumentoOrigem(dav.documentoOrigemNumero, dav.documentoOrigemSerie);
  return (
    <span
      className="flex w-[200px] shrink-0 items-center gap-xs overflow-hidden px-[10px]"
      data-testid="documento-origem-dav"
    >
      {numero === null ? null : (
        <span className="min-w-0 truncate font-mono text-xs font-semibold tabular-nums">
          {numero}
        </span>
      )}
      {rotulo === '' ? null : (
        <Badge tom={TOM_DO_DOCUMENTO[tipo]} testId="badge-tipo-documento">
          {rotulo}
        </Badge>
      )}
    </span>
  );
}

/**
 * Estrutura de layout que o Boneyard fotografa para gerar o shimmer da lista.
 *
 * **Uma linha por item da página, na altura da linha carregada** — não mais seis
 * cartões soltos: assim a área de resultados tem a mesma altura antes e depois
 * de o resultado chegar, e a janela não salta de tamanho no meio da consulta.
 */
function EstruturaResultados(props: { 'aria-hidden'?: boolean }): ReactElement {
  return (
    <ul aria-hidden={props['aria-hidden']}>
      {/* A faixa do cabeçalho da tabela entra no esqueleto (correção do
          usuário, 2026-09-16): sem ela o bloco de carregamento era 38px mais
          curto que o carregado, e as dez linhas apareciam deslocadas para cima
          no instante em que o resultado chegava. Mesma altura e mesmas bordas
          de `TabelaDeDavs`. */}
      <li className="h-[38px] border-y border-border bg-muted" aria-hidden="true" />
      {Array.from({ length: ITENS_POR_PAGINA }, (_, indice) => (
        <li
          key={indice}
          className="flex h-10 items-center border-b border-border px-base last:border-b-0"
        >
          <div className="h-3.5 rounded-sm bg-secondary" style={{ width: `${80 - indice * 5}%` }} />
        </li>
      ))}
    </ul>
  );
}
